'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { SectionWrapper } from '../shared';
import { StaggerContainer, staggerItem } from '../shared/ScrollReveal';
import { useAnalytics } from '@/services/frontendAnalyticsService';

interface Capability {
  label: string;
  title: string;
  description: string;
  image: string;
  bgClass: string;
  href: string;
  imageClassName?: string;
}

const capabilities: Capability[] = [
  {
    label: 'Riset Cepat',
    title: 'Asisten Riset Hukum yang Mengerti Konteks',
    description:
      'Agen AI yang menelusuri database peraturan, mengekstrak pasal relevan, dan menyusun analisis hukum yang akurat dalam hitungan menit. Setiap jawaban dilengkapi dengan sitasi ke peraturan asli.',
    image: '/static/images/landing/features/search-800.webp',
    bgClass: 'bg-[#F4F4F6]',
    href: '/product/riset-cepat',
  },
  {
    label: 'Konsolidasi Aturan',
    title: 'Satu Pandangan untuk Semua Perubahan Peraturan',
    description:
      'Lihat kondisi terkini peraturan dalam satu tampilan terpadu. Gabungkan peraturan induk dengan seluruh perubahan, putusan MK, dan penjelasan—tanpa perlu cross-check manual.',
    image: '/static/images/landing/features/consolidation-800.webp',
    bgClass: 'bg-[#F6F5F2]',
    href: '/product/konsolidasi-aturan',
  },
  {
    label: 'Tabel Ekstraksi',
    title: 'Ubah Tumpukan Dokumen Jadi Wawasan',
    description:
      'Sebuah spreadsheet berteknologi AI yang mengekstrak informasi penting dari ratusan dokumen dalam sekejap. Setiap hasil dilengkapi dengan penjelasan AI dan kutipan langsung dari dokumen asli.',
    image: '/static/images/landing/features/relations-800.webp',
    bgClass: 'bg-[#F2F5F8]',
    href: '/product/tabular-review',
  },
  {
    label: 'Tinjau Dokumen',
    title: 'Analisis Kepatuhan Dokumen yang Mendalam',
    description:
      'AI menganalisis dokumen hukum untuk menemukan pelanggaran regulasi, kontradiksi internal, dan klausa yang hilang dengan kutipan sumber yang terverifikasi.',
    image: '/static/images/landing/features/relations-800.webp',
    bgClass: 'bg-[#F5F2F6]',
    href: '/product/tinjau-dokumen',
  },
];

export function CoreCapabilities() {
  const analytics = useAnalytics();

  return (
    <SectionWrapper background="white" padding="md" containerSize="xl">
      <div className="mb-12 md:mb-16 grid md:grid-cols-[1fr_2fr] gap-6 md:gap-10 items-start">
        <div>
          <h2 className="text-xs font-bold tracking-[0.2em] text-neutral-500 uppercase mt-2">
            WHAT JUSTISIO DOES
          </h2>
        </div>
        <div>
          <h3 className="text-fluid-2xl md:text-fluid-3xl lg:text-fluid-4xl font-medium text-neutral-900 leading-[1.2] tracking-tight max-w-3xl">
            Riset hukum yang mendalam, terhubung dalam satu lapisan kecerdasan unik.
          </h3>
        </div>
      </div>

      <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 lg:gap-x-8 lg:gap-y-16">
        {capabilities.map((cap, idx) => (
          <motion.div key={idx} variants={staggerItem} className="flex flex-col gap-5 group">
            {/* Visual Container - Borderless, Soft, Luxurious */}
            <div
              className={`relative aspect-[4/5] w-full rounded-2xl overflow-hidden ${cap.bgClass} flex items-center justify-center p-0 transition-transform duration-500 ease-out group-hover:scale-[1.04]`}
            >
              <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-md transition-shadow duration-500 ease-out group-hover:shadow-xl">
                <Image
                  src={cap.image}
                  alt={cap.title}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className={`object-cover object-center transition-all duration-700 ease-out ${cap.imageClassName || ''}`}
                />
              </div>
            </div>

            {/* Text Content - Clean, Left-aligned, Elegant typography */}
            <div className="flex flex-col gap-2 px-1">
              <span className="text-fluid-xs font-bold tracking-[0.15em] text-neutral-500">
                {cap.label}
              </span>
              <h4 className="text-fluid-lg md:text-fluid-xl font-medium text-neutral-900 leading-snug">
                {cap.title}
              </h4>
              <p className="text-fluid-sm text-neutral-600 leading-relaxed mt-1">{cap.description}</p>
              <Link
                href={cap.href}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900 hover:text-neutral-600 mt-2 group/link"
                onClick={() => analytics.landingPage.capabilityClicked({ title: cap.title, href: cap.href })}
              >
                baca selengkapnya
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover/link:translate-x-1"
                />
              </Link>
            </div>
          </motion.div>
        ))}
      </StaggerContainer>
    </SectionWrapper>
  );
}
